import classnames from 'classnames';
import { TabPanel } from '../../../components';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Input, Table } from '../../../components/Bootstrap';
import { Checkbox, Navigation, LayoutContent, Layout } from '../../../components';
import { RemoveUserVariables } from './__generated__/RemoveUser';
import { ReSendInviteVariables } from './__generated__/ReSendInvite';
import { SendInviteVariables } from './__generated__/SendInvite';
import { UpdateInviteVariables } from './__generated__/UpdateInvite';
import { UpdateRoleVariables } from './__generated__/UpdateRole';
import { UserListQuery, UserListQuery_users as User, UserListQueryVariables } from './__generated__/UserListQuery';
import { DeleteUser } from './DeleteUser';
import { InfoModal } from './InfoModal';
import './Management.scss';
import { ManageUserModal } from './ManageUserModal';
import { NotificationModal } from '../../../components/NotificationModal';
import { Pencil } from './Pencil';
import { TrashCan } from './TrashCan';
import { AddIcon } from './AddIcon';

interface ManagementRouterParams {
  venueId: string;
}
// UserListQueryVariables
interface ManagementProps extends RouteComponentProps<ManagementRouterParams> {
  loading: boolean;
  data?: UserListQuery;
  error?: Error;
  variables: UserListQueryVariables;
  sendInvite: ({ variables }: { variables: SendInviteVariables }) => void;
  updateRole: ({ variables }: { variables: UpdateRoleVariables }) => void;
  removeUser: ({ variables }: { variables: RemoveUserVariables }) => void;
  resendInvite: ({ variables }: { variables: ReSendInviteVariables }) => void;
  updateInvite: ({ variables }: { variables: UpdateInviteVariables }) => void;
  refetch: Function;
}

interface ManagementState {
  checked: string[];
  selectedUserForDelete?: User[];
  selectedUserForEdit?: User;
  isAdding: boolean;
  showInviteSent: boolean;
  showReinviteSent: boolean;
  showInfoModal: boolean;
}

interface TableRowProps {
  onClick: any;
  user: User;
  checked: boolean;
  onClickEdit: any;
  onClickResend: any;
}

const TableRow = ({ onClick, onClickEdit, onClickResend, user, checked }: TableRowProps) => {
  const onClickCb = React.useCallback(() => {
    onClick(user.id);
  }, [onClick, user]);

  const onClickEditCb = React.useCallback(() => {
    onClickEdit(user);
  }, [onClickEdit, user]);

  const onClickResendCb = React.useCallback(() => {
    onClickResend(user.id);
  }, [onClickResend, user]);

  return (
    <tr
      className={classnames('user-management-row', {
        'user-management-row-selected': checked,
      })}
    >
      <td className='user-management-action-col'>
        <Checkbox onClick={onClickCb} isChecked={checked} />
      </td>
      <td className='user-management-name'>
        <b>
          {user.firstName} {user.lastName}
        </b>
        {!user.inviteAccepted && ' - '}
        {!user.inviteAccepted && (
          <span className='resend-link' onClick={onClickResendCb}>
            Resend Invite
          </span>
        )}
      </td>
      <td>{user.role ? user.role.name : ''}</td>
      <td>{user.email}</td>
      <td className='user-management-row-actions-cell'>
        <Pencil onClick={onClickEditCb} />
      </td>
    </tr>
  );
};

export class Management extends React.PureComponent<ManagementProps, ManagementState> {
  public inviteTimer;

  state: ManagementState = {
    isAdding: false,
    showInviteSent: false,
    showReinviteSent: false,
    showInfoModal: false,
    checked: [],
  };

  componentWillUnmount() {
    if (this.inviteTimer) {
      clearTimeout(this.inviteTimer);
    }
  }

  refetch = () => {
    this.props.refetch({
      vairables: this.props.variables,
    });
  };

  toggle = (tabIndex) => {
    const role = this.props.data.roles[tabIndex - 1];
    const qs = new URLSearchParams({
      role: role ? role.name : '',
      page: this.props.variables.page.toString(),
    });

    this.props.history.push(this.props.location.pathname + '?' + qs.toString());
  };

  onPressAdd = () => {
    this.setState({ isAdding: true });
  };

  onChange = (e) => {
    const qs = new URLSearchParams({
      role: this.props.variables.role ?? '',
      page: this.props.variables.page.toString(),
      search: e.target.value,
    });

    this.props.history.push(this.props.location.pathname + '?' + qs);
  };

  resendInvite = async (inviteId) => {
    this.setState({ showReinviteSent: true });

    await this.props.resendInvite({
      variables: { inviteId },
    });

    setTimeout(() => {
      this.setState({ showReinviteSent: false });
    }, 10000);
  };

  hideModal = () => {
    this.setState({
      isAdding: false,
      selectedUserForDelete: undefined,
      selectedUserForEdit: undefined,
      showInfoModal: false,
    });
  };

  onClickCheckbox = (id) => {
    if (this.state.checked.includes(id)) {
      this.setState({
        checked: this.state.checked.filter((i) => id !== i),
      });
    } else {
      this.setState({
        checked: this.state.checked.concat(id),
      });
    }
  };

  onSubmitInvite = async ({ firstName, lastName, email, role }) => {
    this.setState({ isAdding: false, showInviteSent: true });

    setTimeout(() => {
      this.setState({ showInviteSent: false });
    }, 10000);

    await this.props.sendInvite({
      variables: {
        invite: {
          firstName,
          lastName,
          email,
          roleId: role,
          venueId: Number(this.props.match.params.venueId),
        },
      },
    });

    this.props.refetch();
  };

  hideOverlay = () => {
    this.setState({ showInviteSent: false, showReinviteSent: false });
  };

  onClickEdit = (selectedUserForEdit) => {
    this.setState({ selectedUserForEdit });
  };

  onDelete = async () => {
    const { selectedUserForDelete } = this.state;
    this.setState({ selectedUserForDelete: undefined });

    await Promise.all(
      selectedUserForDelete.map((user) => {
        return this.props.removeUser({
          variables: {
            userId: user.id,
          },
        });
      })
    );

    this.props.refetch();
  };

  onSubmitRoleChange = async ({ id, forInvite, role }) => {
    this.setState({ selectedUserForEdit: undefined });

    if (!forInvite) {
      await this.props.updateRole({
        variables: {
          userId: id,
          roleId: role,
        },
      });
    } else {
      await this.props.updateInvite({
        variables: {
          inviteId: id,
          roleId: role,
        },
      });
    }

    this.props.refetch();
  };

  onClickDelete = () => {
    const selectedUserForDelete = this.props.data.users.filter((user) => {
      return this.state.checked.includes(user.id);
    });

    this.setState({ selectedUserForDelete });
  };

  onClickInfo = () => {
    this.setState({ showInfoModal: true });
  };

  renderTabContent = () => {
    return (
      <div>
        <div className='pb-4 d-flex justify-content-between'>
          <div className='user-search-container'>
            <Input
              value={this.props.variables.search}
              onChange={this.onChange}
              placeholder='Search all users'
              required={true}
              className='user-management-toolbar-input'
            />
          </div>
          <div className='d-none d-md-block'>
            <AddIcon onClick={this.onPressAdd} />
            <TrashCan onClick={this.onClickDelete} />
          </div>
        </div>
        <div className='user-management-container'>
          <Table className='user-management'>
            <thead>
              <tr>
                <th className='user-management-action-col' />
                <th>Name</th>
                <th>Role</th>
                <th className='user-email-col'>Email</th>
                <th className='user-management-pencil-col' />
              </tr>
            </thead>
            <tbody>
              {this.props.data.users.map((user) => (
                <TableRow
                  key={user.id}
                  onClick={this.onClickCheckbox}
                  user={user}
                  checked={this.state.checked.includes(user.id)}
                  onClickEdit={this.onClickEdit}
                  onClickResend={this.resendInvite}
                />
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    );
  };

  renderTitle = (section) => {
    return section.name;
  };

  renderContent() {
    const { data, loading, error } = this.props;
    if ((loading && !data) || error) {
      return 'Loading...';
    }

    const props = {
      fillContainer: false,
      paddingHorizontal: 20,
      activeTab: this.props.variables.role ? data.roles.findIndex((r) => this.props.variables.role === r.name) + 1 : 0,
      sections: [{ id: 'all', name: 'All' }, ...this.props.data.roles],
      renderContent: this.renderTabContent,
      toggle: this.toggle,
      renderTitle: this.renderTitle,
    };

    return (
      <div className='user-management ps-4 pe-4 pt-3 pb-4'>
        <TabPanel {...props} />
        <div className='user-management-info d-none d-md-block' onClick={this.onClickInfo}>
          i
        </div>
        <InfoModal roles={data.roles} open={this.state.showInfoModal} toggle={this.hideModal} />
      </div>
    );
  }

  renderEditModal = () => {
    if (this.state.selectedUserForEdit) {
      return (
        <ManageUserModal
          toggle={this.hideModal}
          onSubmit={this.onSubmitRoleChange}
          user={this.state.selectedUserForEdit}
          roles={this.props.data.roles}
        />
      );
    }
  };

  render() {
    const { data } = this.props;
    const { isAdding, showReinviteSent, showInviteSent } = this.state;

    return (
      <Layout>
        <Navigation />
        <Layout.TopBar>
          <Layout.TopBar.Left>
            <Layout.TopBar.Title>Users</Layout.TopBar.Title>
          </Layout.TopBar.Left>
        </Layout.TopBar>
        <LayoutContent error={this.props.error}>
          {this.renderContent()}
          {isAdding && <ManageUserModal roles={data.roles} toggle={this.hideModal} onSubmit={this.onSubmitInvite} />}
          {this.renderEditModal()}
          <NotificationModal text='Invitation Sent!' onClick={this.hideOverlay} hidden={!showInviteSent} />
          <NotificationModal text='Invitation Resent!' onClick={this.hideOverlay} hidden={!showReinviteSent} />
          <DeleteUser open={!!this.state.selectedUserForDelete} toggle={this.hideModal} onDelete={this.onDelete} />
        </LayoutContent>
      </Layout>
    );
  }
}
